import { forwardRef, RefObject, useImperativeHandle, useRef } from "react";
import { Virtuoso, VirtuosoHandle } from "react-virtuoso";

import { Variable } from "../../types";

import { useVariableFocus } from "./hooks/useVariableFocus";
import InputVariable from "./InputVariable";

export interface VariableListHandle {
  scrollToBottom: () => void;
}

const VariableList = forwardRef<
  VariableListHandle,
  {
    variables: Variable[];
    updateInputVariable: (v: Variable, i: number) => void;
    removeInputVariable: (i: number) => void;
    lastItemRef: React.RefObject<HTMLDivElement> | null;
    hideKey?: boolean;
    className?: string;
    placeholder?: string;
    showAddFileButton?: boolean;
    keyPlaceholder?: string;
    tooltip?: string;
    keyTooltip?: string;
    removeTooltip?: string;
    readOnly?: boolean;
    scrollContainerRef: RefObject<HTMLDivElement>;
    popoverPortalContainer?: HTMLElement | null;
  }
>(
  (
    {
      variables,
      updateInputVariable,
      removeInputVariable,
      lastItemRef,
      hideKey,
      className,
      placeholder,
      showAddFileButton,
      keyPlaceholder,
      tooltip,
      keyTooltip,
      removeTooltip,
      readOnly,
      scrollContainerRef,
      popoverPortalContainer,
    },
    ref,
  ) => {
    const virtuosoRef = useRef<VirtuosoHandle>(null);
    const isVirtualized = variables.length > 50;

    const { registerInputRef, focusNextInput } = useVariableFocus({
      variables,
      virtuosoRef,
      scrollContainerRef,
      isVirtualized,
    });

    useImperativeHandle(
      ref,
      () => ({
        scrollToBottom: () => {
          if (virtuosoRef.current && variables.length > 0) {
            virtuosoRef.current.scrollToIndex({
              index: variables.length - 1,
              behavior: "smooth",
              align: "end",
            });
          }
        },
      }),
      [variables],
    );

    if (variables.length > 50) {
      return (
        <Virtuoso
          ref={virtuosoRef}
          totalCount={variables.length}
          style={{ height: "100%" }}
          overscan={5}
          data={variables}
          // eslint-disable-next-line react/no-unstable-nested-components
          itemContent={(index, varItem) => (
            <div className="pb-2" ref={index === variables.length - 1 ? lastItemRef : null}>
              <InputVariable
                placeholder={placeholder}
                showAddFileButton={showAddFileButton}
                keyPlaceholder={keyPlaceholder}
                tooltip={tooltip}
                keyTooltip={keyTooltip}
                removeTooltip={removeTooltip}
                readOnly={readOnly}
                hideKey={hideKey}
                className={className}
                variable={varItem}
                onChange={v => updateInputVariable(v, index)}
                onDelete={() => removeInputVariable(index)}
                onInputRef={inputRef => registerInputRef(varItem.id, inputRef)}
                onFocusNext={() => focusNextInput(varItem.id)}
                popoverPortalContainer={popoverPortalContainer}
              />
            </div>
          )}
        />
      );
    }

    return (
      <div className="flex w-full flex-col gap-y-2">
        {variables.map((variable, index) => (
          <div key={variable.id} ref={index === variables.length - 1 ? lastItemRef : null}>
            <InputVariable
              placeholder={placeholder}
              showAddFileButton={showAddFileButton}
              keyPlaceholder={keyPlaceholder}
              tooltip={tooltip}
              keyTooltip={keyTooltip}
              removeTooltip={removeTooltip}
              readOnly={readOnly}
              hideKey={hideKey}
              className={className}
              variable={variable}
              onChange={v => updateInputVariable(v, index)}
              onDelete={() => removeInputVariable(index)}
              onInputRef={inputRef => registerInputRef(variable.id, inputRef)}
              onFocusNext={() => focusNextInput(variable.id)}
              popoverPortalContainer={popoverPortalContainer}
            />
          </div>
        ))}
      </div>
    );
  },
);

VariableList.displayName = "VariableList";

export default VariableList;
